import {
  Box,
  Text,
  Accordion,
  AccordionButton,
  Flex,
  AccordionIcon,
  AccordionPanel,
  AccordionItem,
  Button,
  Skeleton,
} from '@everlywell/ui-kit';
import { useCart } from 'components/Cart/Cart.hooks';
import DynamicIcon from 'components/DynamicIcon';
import React, { MouseEventHandler, useState } from 'react';
import type { UseFormMethods } from 'react-hook-form/dist/types';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { Promo, promoAppliesToProduct } from 'utils/helpers/activePromoHelpers';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';
import { Order } from 'utils/types';

import PromoCodeSubmission from './components/PromoCodeSubmission/PromoCodeSubmission';

export type PromoFormValues = {
  promoCode: string;
};

type PromoCodeProps = {
  promotions?: any[];
  promotion?: any; // Deprecated: kept for backward compatibility
  order?: Order;
  showLoadingState?: boolean;
  isLoading?: boolean;
  applyPromoMutation?: any;
  removePromoCodeHandler: MouseEventHandler<HTMLButtonElement>;
  removeSinglePromoHandler?: (promoCode: string) => void;
  handleSubmit: UseFormMethods<PromoFormValues>['handleSubmit'];
  formState: UseFormMethods<PromoFormValues>['formState'];
  control: UseFormMethods<PromoFormValues>['control'];
  clearErrors?: UseFormMethods<PromoFormValues>['clearErrors'];
  promoCodeSubmitHandler: (PromoFormValues: PromoFormValues) => void;
  showAdvertisedPromos?: boolean;
  /**
   * Shows lightGrey border instead of uiGrey
   */
  isVariant?: boolean;
  hidePromoCodeEntry?: boolean;
  hiddenPromoCodeEntryMessage?: string;
};

const PromoCode = ({
  promotions,
  promotion, // Deprecated: kept for backward compatibility
  order: orderProp,
  showLoadingState,
  isLoading,
  applyPromoMutation,
  removePromoCodeHandler,
  removeSinglePromoHandler,
  handleSubmit,
  formState,
  control,
  clearErrors,
  promoCodeSubmitHandler,
  showAdvertisedPromos = false,
  isVariant = false,
  hidePromoCodeEntry = false,
  hiddenPromoCodeEntryMessage,
}: PromoCodeProps) => {
  // Support both new (promotions array) and old (single promotion) API
  const appliedPromotions = promotions || (promotion ? [promotion] : []);
  const [promoMessage, setPromoMessage] = useState('');
  const [wasAdvertisedPromoSubmitted, setWasAdvertisedPromoSubmitted] =
    useState(false);
  const [promoCodeInputValue, setPromoCodeInputValue] = useState('');

  const { order } = useCart();
  const { advertisedPromos, isLoading: isLoadingPromos } = useAdvertisedPromos({
    isEnabled: showAdvertisedPromos,
  });

  const promos = advertisedPromos.filter((promo) => {
    if (!order) return false;

    if (
      appliedPromotions.some(
        (appliedPromotion) => appliedPromotion.code === promo.code,
      )
    ) {
      return false;
    }

    return order.line_items.some((lineItem) => {
      switch (promo.type) {
        case 'product':
          return promoAppliesToProduct(lineItem.product_id!, promo);
        case 'variant':
          return promo.rules.included_variant_ids.includes(lineItem.variant_id);
        default:
          return false;
      }
    });
  });

  const getDiscountText = (promo: Promo) => {
    if (promo.discounts.flat_percent) {
      return `${promo.discounts.flat_percent}% Discount`;
    }

    if (promo.discounts.flat_rate) {
      return `$${promo.discounts.flat_rate} OFF`;
    }

    // Get the best tiered discount
    const tiers = promo.discounts.tiered;
    if (tiers && tiers.length > 0) {
      const bestTier = tiers.reduce(
        (max, tier) => (tier.percent > max.percent ? tier : max),
        tiers[0],
      );
      return `Up to ${bestTier.percent}% Discount`;
    }
  };

  const handlePromoCodeSubmit = (promoFormValues: PromoFormValues) => {
    promoCodeSubmitHandler(promoFormValues);
    setPromoMessage(`${promoFormValues.promoCode} promo code applied`);
    // Clear input value after submission
    setPromoCodeInputValue('');
  };

  const trackAdvertisedPromoClick = (promoCode: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Advertised promo code submitted',
        promoCode,
      },
    });
  };

  const trackAccordionClick = (isOpen: boolean) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_ACCORDION,
      data: {
        label: 'Accordion promo code clicked',
        action: isOpen ? 'to open' : 'to close',
      },
    });
  };

  const advertisedPromosElement =
    showAdvertisedPromos && promos.length > 0 ? (
      <Flex gap="2" marginTop="2" flexDirection="column">
        {promos.map((promo) => (
          <Flex
            key={promo.id}
            justifyContent="space-between"
            alignItems="center"
            padding="3"
            width="100%"
            backgroundColor="tints.white"
            borderRadius="xl"
            minHeight="56px"
            gap="3"
            onClick={() => {
              setPromoCodeInputValue(promo.code);
              handlePromoCodeSubmit({ promoCode: promo.code });
              setWasAdvertisedPromoSubmitted(true);
              trackAdvertisedPromoClick(promo.code);
            }}
            _hover={{
              cursor: 'pointer',
            }}
          >
            <Flex flexDirection="column">
              <Text fontSize="md" fontWeight="medium">
                {getDiscountText(promo)}
              </Text>
              <Text fontSize="sm" color="tints.darkGrey">
                {promo?.description || 'Applicable on purchase'}
              </Text>
            </Flex>
            <Button
              height="32px"
              paddingX="2 !important" // To override the default padding
              paddingY="0.5 !important" // To override the default padding
              backgroundColor="tints.cream"
              color="tints.black"
              variant="secondary"
              boxShadow="none"
              borderRadius="lg"
              _active={{
                boxShadow: 'inset 0 0 0 2px tints.darkGrey',
                color: 'tints.darkGrey',
              }}
              _hover={{
                cursor: 'pointer',
              }}
              fontSize={['md']}
              aria-label={`Apply promo code ${promo.code}`}
            >
              {promo?.code?.toUpperCase() || 'Apply'}
            </Button>
          </Flex>
        ))}
      </Flex>
    ) : null;

  const promoCodeSubmissionElement = (
    <PromoCodeSubmission
      promoMessage={promoMessage}
      promotions={appliedPromotions}
      promotion={appliedPromotions[0]} // Keep for backward compatibility
      order={orderProp || order}
      showLoadingState={showLoadingState}
      isLoading={isLoading}
      applyPromoMutation={applyPromoMutation}
      removePromoCodeHandler={removePromoCodeHandler}
      removeSinglePromoHandler={removeSinglePromoHandler}
      handlePromoCodeSubmit={handleSubmit(handlePromoCodeSubmit)}
      formState={formState}
      control={control}
      clearErrors={clearErrors}
      advertisedPromosElement={advertisedPromosElement}
      wasAdvertisedPromoSubmitted={wasAdvertisedPromoSubmitted}
      promoCodeInputValue={promoCodeInputValue}
      setPromoCodeInputValue={setPromoCodeInputValue}
      shouldShowInputIcon={!showAdvertisedPromos}
      isVariant={isVariant}
      hidePromoCodeEntry={hidePromoCodeEntry}
      hiddenPromoCodeEntryMessage={hiddenPromoCodeEntryMessage}
    />
  );

  return showAdvertisedPromos ? (
    <Accordion
      backgroundColor="tints.cream"
      // Expand by default if there is a promotion applied
      defaultIndex={appliedPromotions.length > 0 ? 0 : undefined}
      borderRadius="xl"
      padding="3"
      allowToggle
    >
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <AccordionButton
              padding="0"
              marginBottom={isExpanded ? 4 : 0}
              sx={{
                // Styles for the chevron icon
                svg: {
                  color: 'tints.uiGrey',
                },
              }}
              onClick={() => trackAccordionClick(isExpanded)}
            >
              <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Flex gap="1">
                  <DynamicIcon icon="Tag" />
                  <Text>Add a promo code</Text>
                </Flex>

                {promos.length > 0 && !isLoadingPromos ? (
                  <Text>{`${promos.length} available`}</Text>
                ) : null}

                {isLoadingPromos ? (
                  <Skeleton height="24px" borderRadius="md" fitContent>
                    <Box width="80px" marginRight="1" />
                  </Skeleton>
                ) : null}
              </Flex>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel paddingBottom="0">
              {promoCodeSubmissionElement}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  ) : (
    promoCodeSubmissionElement
  );
};

export default PromoCode;
